import { fetchJson } from '@api/ApiHelper';
import { ApiData, ApiDatumResponse, ApiResponseObject } from '@api/v4/ApiResponseTypes';

/** Distinguishes newly-issued managed keys from migrated legacy keys. */
export type ApiKeySource = 'managed' | 'legacy';

export type ApiKeyServerStatus =
  | 'active'
  | 'expiring_soon'
  | 'deactivated'
  | 'revoked';

export interface ApiKeyRotatedFrom {
  id: number;
  /** Lifecycle status of the predecessor; omitted by older payloads. */
  status?: ApiKeyServerStatus;
  token_prefix: string;
  type: string;
}

export interface ApiKeyCreatedByUser {
  email: string;
  id: number;
  name: string;
  user_key: string;
}

/** JSON:API `attributes` payload for a single api_key resource. */
export interface ApiKeyAttributes {
  created_at: string;
  created_by_user: ApiKeyCreatedByUser | null;
  description: string | null;
  expires_at: string | null;
  last_used_at: string | null;
  name: string;
  revoked_at: string | null;
  rotated_from: ApiKeyRotatedFrom | null;
  source: ApiKeySource;
  status: ApiKeyServerStatus;
  /** Plaintext token — only returned once, on create and rotate responses. */
  token?: string;
  token_prefix: string;
}

export type ApiKeyResource = ApiResponseObject<ApiKeyAttributes, 'api_key'>;

export type ApiKeysListResponse = ApiData<ApiKeyAttributes, 'api_key'>;

export type ApiKeyResponse = ApiDatumResponse<ApiKeyAttributes, 'api_key'>;

export interface GetApiKeysParams {
  order?: 'asc' | 'desc';
  page?: number;
  per?: number;
  sort?: string;
  /** Filters by one or more statuses; sent as repeated `status[]=` params. */
  status?: ApiKeyServerStatus[];
}

/**
 * Fetches the managed API keys for an organization. Sorting, filtering, and
 * pagination are handled server-side.
 * GET /api/v4/organizations/:organization_key/api_keys
 */
export const getApiKeys = async (
  organizationKey: string,
  { order, page, per, sort, status = [] }: GetApiKeysParams = {}
): Promise<ApiKeysListResponse> => {
  // Built by hand because `fetchJson` joins array params with commas, but the
  // API expects repeated `status[]=active&status[]=expiring_soon` params.
  const query = new URLSearchParams();
  if (per !== undefined) query.append('per', String(per));
  if (page !== undefined) query.append('page', String(page));
  if (sort) query.append('sort', sort);
  if (order) query.append('order', order);
  status.forEach((value) => query.append('status[]', value));

  const queryString = query.toString();

  return fetchJson<ApiKeysListResponse>({
    method: 'GET',
    url: `/api/v4/organizations/${organizationKey}/api_keys${queryString ? `?${queryString}` : ''}`,
  });
};

export interface RotateApiKeyParams {
  /** Legacy keys only: description for the materialized key. */
  description?: string;
  /** ISO 8601 expiry override. Managed keys keep the default when omitted. */
  expiresAt?: string;
  /** Legacy keys only: name for the materialized key. */
  name?: string;
  /** Pass 'legacy' to rotate a migrated key (`id` is the user id). */
  source?: ApiKeySource;
}

/**
 * Rotates an API key, returning the successor key with its one-time plaintext
 * `token`. Managed keys ignore name/description (they are inherited); only
 * `expires_at` is applied. Legacy keys materialize with an editable name.
 * POST /api/v4/organizations/:organization_key/api_keys/:id/rotate
 */
export const rotateApiKey = async (
  organizationKey: string,
  id: string,
  { description, expiresAt, name, source }: RotateApiKeyParams = {}
): Promise<ApiKeyResponse> => {
  // The endpoint reads flat params (name/description/source/expires_at), not a
  // JSON:API-wrapped body.
  const body: Record<string, string> = {};
  if (name) body.name = name;
  if (description) body.description = description;
  if (source) body.source = source;
  if (expiresAt) body.expires_at = expiresAt; // eslint-disable-line @typescript-eslint/naming-convention

  return fetchJson<ApiKeyResponse>({
    body,
    method: 'POST',
    url: `/api/v4/organizations/${organizationKey}/api_keys/${id}/rotate`,
  });
};
